/* eslint-disable import/no-cycle */
/* eslint-disable no-unused-vars */

import {createSlice} from '@reduxjs/toolkit';
import get from 'lodash.get';
import isEmpty from 'lodash.isempty';
import {configurationApi} from '@api/configuration/api';
import {CONFIGURATION_TYPES} from '@api/constants';
import {layoutApi} from '@api/layout/api';
import {transactionApi} from '@api/transaction/api';
import {
    fetchDocument as fetchTemplateDocument,
    refreshDocuments as refreshTemplateDocuments,
    fetchTemplate,
    addApproval as addTemplateApproval,
    removeApproval as removeTemplateApproval,
    updateApprovalInState as updateTemplateApprovalInState,
    addField as addTemplateField,
    removeField as removeTemplateField,
    updateFieldInState as updateTemplateFieldInState,
    clearApprovalsForDocument as clearTemplateApprovalsForDocument
} from '@components/Template/templateSlice';
import {
    fetchDocument as fetchTransactionDocument,
    refreshDocuments as refreshTransactionDocuments,
    fetchTransaction,
    addApproval as addTransactionApproval,
    removeApproval as removeTransactionApproval,
    updateApprovalInState as updateTransactionApprovalInState,
    addField as addTransactionField,
    removeField as removeTransactionField,
    updateFieldInState as updateTransactionFieldInState,
    clearApprovalsForDocument as clearTransactionApprovalsForDocument
} from '@components/Transaction/transactionSlice';
import {getTransactionConditions, updateFieldConditions} from '@api/conditionalField/api';
import {approvalApi} from '@api/approval/api';
import {fieldApi} from '@api/field/api';
import {customFieldApi} from '@api/customField/api';
import {accountUserApi} from '@api/accountUser/api';
import {getErrorMessage} from '@utils/requests/error';
import {
    TRANSACTION_STATUSES,
    PACKAGE_TYPE_NAME,
    RECIPIENT_TYPES,
    FETCH_OPTIONS_STEP
} from '@constants/index';
import {getFieldById} from './utils';
import {buildFieldRequest, convertBinding} from './utils/buildFieldRequest';
import {buildApprovalRequest} from './utils/buildApprovalRequest';

export const designerSlice = createSlice({
    name: 'designer',
    initialState: {
        settings: {},
        selectedInfo: {},
        fetchDocumentEque: {
            documentIds: [],
            eque: [],
            selectedInfo: {}
        },
        copiedFieldInfo: {},
        customFields: [],
        validationTypes: [],
        customFieldUserValues: {},
        layouts: [],
        packageType: '',
        activeDocument: {
            id: null,
            pageIndex: 0
        },
        isFetchingLayouts: false,
        fetchLayoutsError: null,
        isSavingLayout: false,
        saveLayoutError: null,
        isApplyingLayout: false,
        applyLayoutError: null,
        isDeletingLayout: false,
        deleteLayoutError: null,
        layoutsMeta: null,
        isFetchingSettings: false,
        fetchSettingsErrors: null,
        isSendingToSign: false,
        sendToSignErrors: null,
        isUpdatingField: false,
        updateApprovalsError: null,
        updateConditionError: null,
        hasPlacedSignature: false,
        createApprovalError: null,
        isFieldCreating: false,
        duplicationFieldIdInProgress: '',
        transactionConditions: [],
        hoveredConditionFieldIds: []
    },
    reducers: {
        setSettings: (state, action) => {
            state.settings = action.payload;
        },
        setCustomFields: (state, action) => {
            state.customFields = action.payload;
        },
        setValidationTypes: (state, action) => {
            state.validationTypes = action.payload;
        },
        setCustomFieldUserValues: (state, action) => {
            state.customFieldUserValues = {
                ...state.customFieldUserValues,
                ...action.payload
            };
        },
        setIsSendingToSign: (state, action) => {
            state.isSendingToSign = action.payload;
        },
        setSendToSignErrors: (state, action) => {
            state.sendToSignErrors = action.payload;
        },
        setIsFetchingSettings: (state, action) => {
            state.isFetchingSettings = action.payload;
        },
        setFetchSettingsErrors: (state, action) => {
            state.fetchSettingsErrors = action.payload;
        },
        setActiveDocument: (state, action) => {
            state.activeDocument = action.payload;
        },
        setIsUpdatingField: (state, action) => {
            state.isUpdatingField = action.payload;
        },
        setUpdateApprovalsError: (state, action) => {
            state.updateApprovalsError = action.payload;
        },
        setSelectedInfo: (state, action) => {
            if (action.payload.unSelect) {
                state.selectedInfo = {};
            } else {
                state.selectedInfo = {
                    ...state.selectedInfo,
                    ...action.payload
                };
            }
        },
        setCopiedFieldInfo: (state, action) => {
            state.copiedFieldInfo = action.payload;
        },
        setFetchDocumentEque: (state, action) => {
            const {
                documentId, selectedInfo, shouldSkipMessage, popQueue
            } = action.payload;
            const fetchState = state.fetchDocumentEque;

            if (popQueue) {
                fetchState.eque.pop();
                fetchState.shouldSkipMessage = true;
                return;
            }
            if (action.payload.fetched) {
                state.fetchDocumentEque = {
                    documentIds: [],
                    eque: [],
                    selectedInfo: {},
                    shouldSkipMessage: false
                };
            }
            if (documentId) {
                state.fetchDocumentEque = {
                    documentIds: state.fetchDocumentEque.documentIds.includes(documentId)
                        ? [...state.fetchDocumentEque.documentIds]
                        : [...state.fetchDocumentEque.documentIds, documentId],

                    eque: [...state.fetchDocumentEque.eque, true],
                    selectedInfo,
                    shouldSkipMessage
                };
            } else {
                state.fetchDocumentEque = {
                    documentIds: [
                        ...state.fetchDocumentEque.documentIds
                    ],
                    eque: [...state.fetchDocumentEque.eque.splice(0, state.fetchDocumentEque.eque.length - 1)],
                    selectedInfo,
                    shouldSkipMessage
                };
            }
        },
        setLayouts: (state, action) => {
            const {payload} = action;
            if (payload.addLayout) {
                const removeLayoutIds = payload.removeLayoutIds || [];
                state.layouts = [
                    payload.data,
                    ...state.layouts.filter((layout) => !removeLayoutIds.includes(layout.id))
                ];
            } else if (payload.reset) {
                state.layouts = [];
            } else if (payload.page <= 1) {
                state.layouts = [...payload.data];
            } else {
                state.layouts = [...state.layouts, ...payload.data];
            }
        },
        setLayoutsMeta: (state, action) => {
            state.layoutsMeta = action.payload;
        },
        setIsDeletingLayout: (state, action) => {
            state.isDeletingLayout = action.payload;
        },
        setDeleteLayoutError: (state, action) => {
            state.deleteLayoutError = action.payload;
        },
        setIsFetchingLayouts: (state, action) => {
            state.isFetchingLayouts = action.payload;
        },
        setFetchLayoutsError: (state, action) => {
            state.fetchLayoutsError = action.payload;
        },
        setIsSavingLayout: (state, action) => {
            state.isSavingLayout = action.payload;
        },
        setSaveLayoutError: (state, action) => {
            state.saveLayoutError = action.payload;
        },
        setIsApplyingLayout: (state, action) => {
            state.isApplyingLayout = action.payload;
        },
        setApplyLayoutError: (state, action) => {
            state.applyLayoutError = action.payload;
        },
        setPackageType: (state, action) => {
            state.packageType = action.payload;
        },
        setUpdateConditionError: (state, action) => {
            state.updateConditionError = action.payload;
        },
        setHasPlacedSignature: (state, action) => {
            state.hasPlacedSignature = action.payload;
        },
        setCreateApprovalError: (state, action) => {
            state.createApprovalError = action.payload;
        },
        setIsFieldCreating: (state, action) => {
            state.isFieldCreating = action.payload;
        },
        setDuplicationFieldIdInProgress: (state, action) => {
            state.duplicationFieldIdInProgress = action.payload;
        },
        setTransactionConditions: (state, action) => {
            state.transactionConditions = action.payload;
        },
        setHoveredConditionFieldIds: (state, action) => {
            state.hoveredConditionFieldIds = action.payload;
        }
    }
});

export const {
    setSettings,
    setCustomFields,
    setValidationTypes,
    setCustomFieldUserValues,
    setActiveDocument,
    setIsSendingToSign,
    setSendToSignErrors,
    setIsFetchingSettings,
    setFetchSettingsErrors,
    setIsUpdatingField,
    setUpdateApprovalsError,
    setSelectedInfo,
    setCopiedFieldInfo,
    setFetchDocumentEque,
    setIsPreventScroll,
    setLayouts,
    setLayoutsMeta,
    setIsFetchingLayouts,
    setFetchLayoutsError,
    setIsSavingLayout,
    setSaveLayoutError,
    setIsApplyingLayout,
    setApplyLayoutError,
    setIsDeletingLayout,
    setDeleteLayoutError,
    setPackageType,
    setUpdateConditionError,
    setHasPlacedSignature,
    setCreateApprovalError,
    setIsFieldCreating,
    setDuplicationFieldIdInProgress,
    setTransactionConditions,
    setHoveredConditionFieldIds
} = designerSlice.actions;

const fetchPackage = (params) => async (dispatch, getState) => {
    const {packageType} = getState().designer;
    if (packageType === PACKAGE_TYPE_NAME.TRANSACTION) {
        const wasSuccessful = await dispatch(fetchTransaction(params));
        return wasSuccessful;
    } if (packageType === PACKAGE_TYPE_NAME.TEMPLATE) {
        const wasSuccessful = await dispatch(fetchTemplate(params));
        return wasSuccessful;
    }
};

const fetchDocument = (packageId, documentId, packageDataType) => async (dispatch, getState) => {
    const packageType = packageDataType || getState().designer.packageType;
    if (packageType === PACKAGE_TYPE_NAME.TRANSACTION) {
        await dispatch(fetchTransactionDocument(packageId, documentId));
    } if (packageType === PACKAGE_TYPE_NAME.TEMPLATE) {
        await dispatch(fetchTemplateDocument(packageId, documentId));
    }
};

const fetchDocuments = (packageDataType) => async (dispatch, getState) => {
    const packageType = packageDataType || getState().designer.packageType;
    if (packageType === PACKAGE_TYPE_NAME.TRANSACTION) {
        await dispatch(refreshTransactionDocuments());
    } else if (packageType === PACKAGE_TYPE_NAME.TEMPLATE) {
        await dispatch(refreshTemplateDocuments());
    }
};

const getPackageActions = (packageType) => (
    packageType === PACKAGE_TYPE_NAME.TEMPLATE
        ? {
            addApproval: addTemplateApproval,
            removeApproval: removeTemplateApproval,
            updateApprovalInState: updateTemplateApprovalInState,
            addField: addTemplateField,
            removeField: removeTemplateField,
            updateFieldInState: updateTemplateFieldInState,
            clearApprovalsForDocument: clearTemplateApprovalsForDocument
        }
        : {
            addApproval: addTransactionApproval,
            removeApproval: removeTransactionApproval,
            updateApprovalInState: updateTransactionApprovalInState,
            addField: addTransactionField,
            removeField: removeTransactionField,
            updateFieldInState: updateTransactionFieldInState,
            clearApprovalsForDocument: clearTransactionApprovalsForDocument
        }
);

export const setFetchDocumentEqueInfo = ({
    documentId = '', selectedInfo = {}, shouldSkipMessage, popQueue
}) => (dispatch) => {
    dispatch(setFetchDocumentEque({
        documentId, selectedInfo, shouldSkipMessage, popQueue
    }));
};

export const deleteFetchDocumentEque = () => async (dispatch) => {
    dispatch(setFetchDocumentEque({fetched: true}));
    dispatch(setIsFieldCreating(false));
};

const getSendToSignErrorMessageKey = (error) => {
    const errorData = get(error, 'response.data', {});
    const {messageKey, message} = errorData;
    const isUnexpectedError = messageKey === 'Unexpected Error';

    if (isUnexpectedError && message) {
        return message;
    }

    return getErrorMessage(error, true).messageKey;
};

export const sendToSign = (packageId) => async (dispatch, getState) => {
    const state = getState();
    const accountId = get(state, 'user.account.id');
    const userId = get(state, 'user.user.id');

    dispatch(setIsSendingToSign(true));
    dispatch(setSendToSignErrors(null));

    try {
        await transactionApi.sendTransaction({
            accountId,
            userId,
            transactionId: packageId
        });
        dispatch(fetchPackage({
            params: {packageId}
        }));
        return true;
    } catch (err) {
        dispatch(setSendToSignErrors([getSendToSignErrorMessageKey(err)]));
        return false;
    } finally {
        dispatch(setIsSendingToSign(false));
    }
};

export const fetchDesignerSettings = ({
    headers, baseUrl, params, options = {}
} = {}) => async (dispatch, getState) => {
    const {activeAccountId} = getState().user;

    dispatch(setIsFetchingSettings(true));
    dispatch(setFetchSettingsErrors(null));

    try {
        const result = await configurationApi.getConfiguration(
            activeAccountId,
            CONFIGURATION_TYPES.DESIGNER_SETTINGS,
            {...options, headers}
        );

        dispatch(setSettings(result.data));
        dispatch(setIsFetchingSettings(false));
        return true;
    } catch (err) {
        console.log('fetchDesignerSettings error', err);

        dispatch(setIsFetchingSettings(false));
        dispatch(setFetchSettingsErrors(getErrorMessage(err)));
        return false;
    }
};

export const fetchValidationTypes = () => async (dispatch) => {
    try {
        const result = await fieldApi.getValidationTypes();
        dispatch(setValidationTypes(Array.isArray(result.data) ? result.data : []));
        return true;
    } catch (err) {
        return false;
    }
};

export const fetchCustomFields = () => async (dispatch, getState) => {
    const accountId = get(getState(), 'user.account.id');

    try {
        const result = await customFieldApi.getCustomFields({accountId});
        dispatch(setCustomFields(result.data));
        return true;
    } catch (err) {
        return false;
    }
};

export const fetchCustomFieldUserValues = (recipients = []) => async (dispatch, getState) => {
    const state = getState();
    const accountId = get(state, 'user.account.id');
    const currentUserId = get(state, 'user.user.id');
    const customFields = get(state, 'designer.customFields', []);
    const cachedValues = get(state, 'designer.customFieldUserValues', {});

    const uncachedRecipients = recipients.filter((recipient) => recipient?.id && !cachedValues[recipient.id]);

    if (isEmpty(customFields) || isEmpty(uncachedRecipients) || !currentUserId) {
        return;
    }

    try {
        const accountUserResponse = await accountUserApi.getAccountUserByAccountAndUser({
            accountId,
            userId: currentUserId
        });
        const accountUserId = get(accountUserResponse, 'data.id');

        if (!accountUserId) {
            return;
        }

        const valuesResponse = await customFieldApi.getUserCustomFields({accountUserId});
        const values = (valuesResponse.data || []).reduce((acc, item) => {
            acc[item.custom_field_id] = item.value;
            return acc;
        }, {});

        const valuesByRecipient = uncachedRecipients.reduce((acc, recipient) => {
            acc[recipient.id] = values;
            return acc;
        }, {});

        dispatch(setCustomFieldUserValues(valuesByRecipient));
    } catch (err) {
        console.log('fetchCustomFieldUserValues error', err);
    }
};

export const getCurrentFieldState = ({
    documentId,
    fieldId
}) => (dispatch, getState) => {
    const state = getState();
    const {packageType} = state.designer;
    const approvalsByDoc = get(state, `${packageType}.approvals`, {});
    const fieldsByApproval = get(state, `${packageType}.fields`, {});
    const documentApprovals = approvalsByDoc[documentId] || [];

    const matchingApproval = documentApprovals.find((approval) => approval.id === fieldId);
    if (matchingApproval) {
        return matchingApproval;
    }

    for (const approval of documentApprovals) {
        const fields = fieldsByApproval[approval.id] || [];
        const field = fields.find(({id}) => id === fieldId);
        if (field) return field;
    }
    return null;
};

export const getActiveDocument = () => (dispatch, getState) => {
    const {packageType, activeDocument} = getState().designer;

    return {
        id: activeDocument.id || get(getState()[packageType], `${packageType}.documents[0].id`),
        pageIndex: activeDocument.pageIndex
    };
};

export const getIsFieldCreating = () => (dispatch, getState) => {
    const {designer} = getState();
    return designer.isFieldCreating;
};

export const getPageFields = (activeDocumentId, activeDocumentPageIndex) => (dispatch, getState) => {
    const state = getState();
    const {packageType} = state.designer;
    const approvalsByDoc = get(state, `${packageType}.approvals`, {});
    const fieldsByApproval = get(state, `${packageType}.fields`, {});
    const documentApprovals = approvalsByDoc[activeDocumentId] || [];
    const documentFields = [];

    documentApprovals.forEach((approval) => {
        const approvalFields = fieldsByApproval[approval.id] || [];
        if (approval.signing_type && approval.signing_type !== 'accept') {
            documentFields.push({
                documentId: activeDocumentId,
                approvalId: approval.id,
                recipientId: approval.recipient_id,
                id: approval.id,
                signing_type: approval.signing_type,
                page: approval.page,
                top: approval.top,
                left: approval.left,
                width: approval.width,
                height: approval.height,
                font_size: approval.font_size,
                is_optional: approval.is_optional,
                isSignatureApproval: true
            });
        }
        approvalFields.forEach((field) => documentFields.push({
            documentId: activeDocumentId,
            approvalId: approval.id,
            recipientId: approval.recipient_id,
            ...field
        }));
    });

    return documentFields.filter((field) => field.page === activeDocumentPageIndex);
};

export const updateField = ({
    packageId,
    documentId,
    approvalId,
    field
}) => async (dispatch, getState) => {
    const {packageType} = getState().designer;
    const actions = getPackageActions(packageType);
    dispatch(setIsUpdatingField(true));
    try {
        const reqBody = buildFieldRequest(field);
        const result = await fieldApi.updateField(packageId, documentId, approvalId, field.id, reqBody);
        dispatch(actions.updateFieldInState({approvalId, field: result.data}));
        dispatch(setIsUpdatingField(false));
        return true;
    } catch (err) {
        dispatch(setIsUpdatingField(false));
        return false;
    }
};

export const updateFieldPosition = ({
    packageId,
    documentId,
    approvalId,
    field,
    recipientId
}) => async (dispatch, getState) => {
    const state = getState();
    const {packageType} = state.designer;
    const actions = getPackageActions(packageType);
    const fieldsByApproval = get(state, `${packageType}.fields`, {});
    const previousField = (fieldsByApproval[approvalId] || []).find(({id}) => id === field.id);
    dispatch(setFetchDocumentEqueInfo({documentId}));
    dispatch(actions.updateFieldInState({approvalId, field}));
    try {
        const body = {
            top: field.top,
            left: field.left,
            width: field.width,
            height: field.height,
            page: field.page
        };
        if (field.is_auto_field != null) {
            body.is_auto_field = field.is_auto_field;
        }
        const binding = convertBinding(field.binding);
        if (binding) {
            body.binding = binding;
        }
        const result = await fieldApi.updateField(packageId, documentId, approvalId, field.id, body);
        dispatch(actions.updateFieldInState({approvalId, field: result.data}));
        dispatch(setFetchDocumentEqueInfo({
            selectedInfo: {
                fieldId: result.data.id, approvalId, documentId, recipientId
            }
        }));
        return true;
    } catch (err) {
        if (previousField) {
            dispatch(actions.updateFieldInState({approvalId, field: previousField}));
        }
        dispatch(setFetchDocumentEqueInfo({popQueue: true}));
        await dispatch(fetchDocument(packageId, documentId));
        return false;
    }
};

export const deleteField = ({
    packageId,
    documentId,
    approvalId,
    fieldId
}) => async (dispatch, getState) => {
    const {packageType} = getState().designer;
    const actions = getPackageActions(packageType);
    try {
        await fieldApi.deleteField(packageId, documentId, approvalId, fieldId);
        dispatch(actions.removeField({approvalId, fieldId}));
        return true;
    } catch (err) {
        return false;
    }
};
export const fetchDesignerTransactionConditions = (transactionId) => async (dispatch) => {
    try {
        const response = await getTransactionConditions(transactionId);
        dispatch(setTransactionConditions(response?.data?.conditions || []));
    } catch {
        dispatch(setTransactionConditions([]));
    }
};

export const updateFieldCondition = ({
    transactionId,
    documentId,
    approvalId,
    fieldId,
    conditions
}) => async (dispatch) => {
    dispatch(setIsUpdatingField(true));

    try {
        await updateFieldConditions(
            transactionId,
            documentId,
            approvalId,
            fieldId,
            {conditions}
        );
        await dispatch(fetchDesignerTransactionConditions(transactionId));
        return true;
    } catch (err) {
        dispatch(setUpdateConditionError(getErrorMessage(err)));
        dispatch(setIsUpdatingField(false));
        return false;
    }
};

export const createField = ({
    packageId,
    documentId,
    approvalId,
    field,
    recipientId,
    isDuplicate
}) => async (dispatch, getState) => {
    const {packageType} = getState().designer;
    const actions = getPackageActions(packageType);
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const tempField = {...field, id: tempId, approval_id: approvalId};
    await dispatch(setFetchDocumentEqueInfo({documentId}));
    dispatch(setIsFieldCreating(true));
    dispatch(actions.addField({approvalId, field: tempField}));
    try {
        const reqBody = buildFieldRequest(field);
        const result = await fieldApi.createField(packageId, documentId, approvalId, reqBody);
        dispatch(actions.removeField({approvalId, fieldId: tempId}));
        dispatch(actions.addField({approvalId, field: result.data}));
        await dispatch(setFetchDocumentEqueInfo({
            selectedInfo: isDuplicate ? {} : {
                fieldId: result.data.id, documentId, approvalId, recipientId
            },
            shouldSkipMessage: isDuplicate
        }));
        dispatch(setIsFieldCreating(false));
        return true;
    } catch (err) {
        dispatch(actions.removeField({approvalId, fieldId: tempId}));
        dispatch(setIsFieldCreating(false));
        dispatch(setFetchDocumentEqueInfo({popQueue: true}));
        return false;
    }
};

const SIGNATURE_SIGNING_TYPES = new Set(['signature', 'initials', 'paraph', 'capture', 'mobile_capture', 'seal']);


export const createApproval = ({
    packageId,
    documentId,
    approval,
    fields: approvalFields,
    isDuplicate
}) => async (dispatch, getState) => {
    const {packageType} = getState().designer;
    const actions = getPackageActions(packageType);
    await dispatch(setFetchDocumentEqueInfo({documentId}));
    dispatch(setIsFieldCreating(true));

    const sourceFields = approvalFields || [];
    const firstField = sourceFields[0];
    if (!firstField) {
        dispatch(setIsFieldCreating(false));
        return false;
    }

    const firstSigningType = String(firstField.signing_type || '').toLowerCase();
    const isSignature = SIGNATURE_SIGNING_TYPES.has(firstSigningType);

    const existingApprovals = getState()[packageType]?.approvals?.[documentId] || [];
    const existingFieldsByApprovalId = getState()[packageType]?.fields || {};
    const existingAcceptOnly = existingApprovals.find((a) => (
        a.recipient_id === approval.recipient_id
        && a.signing_type === 'accept'
        && isEmpty(existingFieldsByApprovalId[a.id])
    ));
    if (existingAcceptOnly) {
        try {
            await approvalApi.deleteApproval(packageId, documentId, existingAcceptOnly.id);
        } catch {}
        dispatch(actions.removeApproval({documentId, approvalId: existingAcceptOnly.id}));
    }

    const tempApprovalId = `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const tempApproval = isSignature ? {
        ...approval,
        id: tempApprovalId,
        signing_type: firstField.signing_type,
        top: firstField.top,
        left: firstField.left,
        width: firstField.width,
        height: firstField.height,
        page: firstField.page,
        is_optional: approval.is_optional ?? false
    } : {
        ...approval,
        id: tempApprovalId,
        signing_type: 'accept',
        top: 0,
        left: 0,
        width: 0,
        height: 0,
        page: firstField.page ?? 0,
        is_optional: approval.is_optional ?? false
    };
    dispatch(actions.addApproval({documentId, approval: tempApproval}));
    if (!isSignature) {
        sourceFields.forEach((f) => {
            const tempFieldId = `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
            dispatch(actions.addField({
                approvalId: tempApprovalId,
                field: {...f, id: tempFieldId, approval_id: tempApprovalId}
            }));
        });
    }

    try {
        let approvalResult;
        let firstFieldId = null;

        if (isSignature) {
            const reqBody = buildApprovalRequest({
                recipient_id: approval.recipient_id,
                is_optional: approval.is_optional,
                signatureField: firstField,
                name: approval.name,
                deprecated_id: approval.deprecated_id
            });
            approvalResult = await approvalApi.createApproval(packageId, documentId, reqBody);
            dispatch(actions.removeApproval({documentId, approvalId: tempApprovalId}));
            dispatch(actions.addApproval({documentId, approval: approvalResult.data}));
        } else {
            const reqBody = buildApprovalRequest({
                recipient_id: approval.recipient_id,
                is_optional: approval.is_optional,
                signatureField: {
                    signing_type: 'accept',
                    width: 0,
                    height: 0,
                    page: firstField.page ?? 0,
                    top: 0,
                    left: 0,
                    extractAnchor: null
                },
                name: approval.name,
                deprecated_id: approval.deprecated_id
            });
            approvalResult = await approvalApi.createApproval(packageId, documentId, reqBody);
            dispatch(actions.removeApproval({documentId, approvalId: tempApprovalId}));
            dispatch(actions.addApproval({documentId, approval: approvalResult.data}));

            const fieldResults = await Promise.all(
                sourceFields.map((f) => fieldApi.createField(packageId, documentId, approvalResult.data.id, buildFieldRequest(f)))
            );
            fieldResults.forEach((fieldResult) => {
                dispatch(actions.addField({approvalId: approvalResult.data.id, field: fieldResult.data}));
            });
            firstFieldId = fieldResults[0]?.data?.id ?? null;
        }

        await dispatch(setFetchDocumentEqueInfo({
            selectedInfo: isDuplicate ? {} : {
                fieldId: isSignature ? approvalResult.data.id : firstFieldId,
                approvalId: approvalResult.data.id,
                recipientId: approvalResult.data.recipient_id,
                documentId
            },
            shouldSkipMessage: isDuplicate
        }));
        dispatch(setIsFieldCreating(false));
        return true;
    } catch (err) {
        dispatch(actions.removeApproval({documentId, approvalId: tempApprovalId}));
        dispatch(setIsFieldCreating(false));
        dispatch(setCreateApprovalError(getErrorMessage(err)));
        dispatch(setFetchDocumentEqueInfo({popQueue: true}));
        return false;
    }
};

export const updateAcceptOnlyRecipient = ({
    packageId, documentId, recipientId, isAcceptOnly
}) => async (dispatch, getState) => {
    const {packageType} = getState().designer;
    const documentApprovals = getState()[packageType]?.approvals?.[documentId] || [];
    const fieldsByApprovalId = getState()[packageType]?.fields || {};

    dispatch(setUpdateApprovalsError(null));
    dispatch(setIsUpdatingField(true));

    try {
        if (isAcceptOnly) {
            await approvalApi.createApproval(packageId, documentId, buildApprovalRequest({
                recipient_id: recipientId,
                is_optional: false,
                signatureField: {
                    signing_type: 'accept',
                    width: 0,
                    height: 0,
                    page: 0,
                    top: 0,
                    left: 0
                }
            }));
        } else {
            const acceptOnlyApprovals = documentApprovals.filter((approval) => (
                approval.recipient_id === recipientId
                && approval.signing_type === 'accept'
                && isEmpty(fieldsByApprovalId[approval.id])
            ));
            await Promise.all(acceptOnlyApprovals.map((approval) => (
                approvalApi.deleteApproval(packageId, documentId, approval.id)
            )));
        }

        await dispatch(fetchDocument(packageId, documentId));

        return true;
    } catch (err) {
        dispatch(setUpdateApprovalsError(getErrorMessage(err)));
        return false;
    } finally {
        dispatch(setIsUpdatingField(false));
    }
};

export const updateApproval = ({
    packageId,
    documentId,
    approvalId,
    payload
}) => async (dispatch, getState) => {
    const {packageType} = getState().designer;
    const actions = getPackageActions(packageType);
    dispatch(setUpdateApprovalsError(null));
    dispatch(setIsUpdatingField(true));

    try {
        const result = await approvalApi.updateApproval(packageId, documentId, approvalId, payload);
        dispatch(actions.updateApprovalInState({documentId, approval: result.data}));
        dispatch(setIsUpdatingField(false));
        return true;
    } catch (err) {
        dispatch(setIsUpdatingField(false));
        dispatch(setUpdateApprovalsError(err));
        return false;
    }
};

export const updateApprovalPosition = ({
    packageId,
    documentId,
    approvalId,
    approval,
    recipientId
}) => async (dispatch, getState) => {
    const state = getState();
    const {packageType} = state.designer;
    const actions = getPackageActions(packageType);
    const approvalsByDoc = get(state, `${packageType}.approvals`, {});
    const previousApproval = (approvalsByDoc[documentId] || []).find(({id}) => id === approvalId);
    dispatch(setFetchDocumentEqueInfo({documentId}));
    dispatch(actions.updateApprovalInState({documentId, approval}));
    try {
        const result = await approvalApi.updateApproval(packageId, documentId, approvalId, {
            top: approval.top,
            left: approval.left,
            width: approval.width,
            height: approval.height,
            page: approval.page
        });
        dispatch(actions.updateApprovalInState({documentId, approval: result.data}));
        dispatch(setFetchDocumentEqueInfo({
            selectedInfo: {
                fieldId: result.data.id, approvalId, documentId, recipientId
            }
        }));
        return true;
    } catch (err) {
        if (previousApproval) {
            dispatch(actions.updateApprovalInState({documentId, approval: previousApproval}));
        }
        dispatch(setFetchDocumentEqueInfo({popQueue: true}));
        return false;
    }
};

export const deleteApproval = ({
    packageId,
    documentId,
    approvalId
}) => async (dispatch, getState) => {
    const {packageType} = getState().designer;
    const actions = getPackageActions(packageType);
    dispatch(setUpdateApprovalsError(null));
    dispatch(setIsUpdatingField(true));
    try {
        await approvalApi.deleteApproval(packageId, documentId, approvalId);
        dispatch(actions.removeApproval({documentId, approvalId}));
        dispatch(setIsUpdatingField(false));
        return true;
    } catch (err) {
        dispatch(setIsUpdatingField(false));
        dispatch(setUpdateApprovalsError(getErrorMessage()));
        return false;
    }
};

export const updateApprovals = ({
    packageId,
    documentId,
    payload
}) => async (dispatch, getState) => {
    const {packageType} = getState().designer;
    const actions = getPackageActions(packageType);
    dispatch(setUpdateApprovalsError(null));
    dispatch(setIsUpdatingField(true));
    try {
        const results = await Promise.all(
            (payload || []).map((approval) => approvalApi.updateApproval(packageId, documentId, approval.id, approval))
        );
        results.forEach((result) => {
            dispatch(actions.updateApprovalInState({documentId, approval: result.data}));
        });
        dispatch(setIsUpdatingField(false));
        return true;
    } catch (err) {
        dispatch(setIsUpdatingField(false));
        dispatch(setUpdateApprovalsError(err));
        return false;
    }
};

export const deleteAllApprovalsAndFields = ({
    packageId,
    documentId
}) => async (dispatch, getState) => {
    const {packageType} = getState().designer;
    const actions = getPackageActions(packageType);
    dispatch(setUpdateApprovalsError(null));
    dispatch(setIsUpdatingField(true));
    try {
        const currentApprovals = get(getState(), `${packageType}.approvals[${documentId}]`, []);
        await Promise.all(
            currentApprovals.map((a) => approvalApi.deleteApproval(packageId, documentId, a.id))
        );
        dispatch(actions.clearApprovalsForDocument({documentId}));
        dispatch(setIsUpdatingField(false));
        return true;
    } catch (err) {
        dispatch(setIsUpdatingField(false));
        dispatch(setUpdateApprovalsError(err));
        return false;
    }
};

export const fetchLayouts = ({
    params = {}, options = {}
} = {}) => async (dispatch) => {
    const {
        search,
        summary = true,
        page = 1,
        // eslint-disable-next-line camelcase
        per_page = FETCH_OPTIONS_STEP,
        shared = false,
        sort,
        shouldSkipLayoutsSet
    } = params;

    dispatch(setIsFetchingLayouts(true));
    dispatch(setFetchLayoutsError(null));

    try {
        const queryParams = {
            summary,
            page,
            // eslint-disable-next-line camelcase
            per_page,
            shared
        };

        if (search) {
            queryParams.search = search;
        }
        if (sort) {
            queryParams.sort = sort;
        }

        const result = await layoutApi.getLayouts(queryParams, options);

        if (!shouldSkipLayoutsSet) {
            dispatch(setLayouts({page, data: result.data}));
            dispatch(setLayoutsMeta(result.meta));
        }
        dispatch(setIsFetchingLayouts(false));
        return result.data;
    } catch (err) {
        dispatch(setIsFetchingLayouts(false));
        dispatch(setFetchLayoutsError(getErrorMessage(err)));
        return false;
    }
};

export const saveLayout = ({
    packageId,
    documentId,
    layoutIds = [],
    name,
    description = '',
    isShared = false
}) => async (dispatch) => {
    dispatch(setIsSavingLayout(true));
    dispatch(setSaveLayoutError(null));

    try {
        const result = await layoutApi.createLayout({
            transaction_id: packageId,
            document_id: documentId,
            name,
            description,
            shared: isShared
        });

        await Promise.all(layoutIds.map((layoutId) => layoutApi.deleteLayout(layoutId)));

        dispatch(setIsSavingLayout(false));
        if (result.data) {
            dispatch(setLayouts({addLayout: true, data: result.data, removeLayoutIds: layoutIds}));
        }

        return true;
    } catch (err) {
        dispatch(setIsSavingLayout(false));
        dispatch(setSaveLayoutError('esl.feedback.layout_save.error'));

        return false;
    }
};

export const applyLayout = ({
    packageId,
    documentId,
    layoutId
}) => async (dispatch) => {
    dispatch(setIsApplyingLayout(true));
    dispatch(setApplyLayoutError(null));

    try {
        await dispatch(deleteAllApprovalsAndFields({packageId, documentId}));

        await layoutApi.applyLayout(layoutId, {
            transaction_id: packageId,
            document_id: documentId
        });
        await dispatch(fetchDocument(packageId, documentId));
        await dispatch(fetchDesignerTransactionConditions(packageId));
        dispatch(setIsApplyingLayout(false));

        return true;
    } catch (err) {
        dispatch(setIsApplyingLayout(false));
        dispatch(setApplyLayoutError(getErrorMessage(err)));

        return false;
    }
};

export const removeLayout = ({layoutId}) => async (dispatch) => {
    dispatch(setIsDeletingLayout(true));
    dispatch(setDeleteLayoutError(null));

    try {
        await layoutApi.deleteLayout(layoutId);
        dispatch(setIsDeletingLayout(false));

        return true;
    } catch (err) {
        dispatch(setIsDeletingLayout(false));
        dispatch(setDeleteLayoutError(getErrorMessage(err)));

        return false;
    }
};

export const getDefaultActiveDocumentId = (state) => {
    const packageType = get(state, 'designer.packageType');
    return get(state, `${packageType}.${packageType}.documents[0].id`);
};

export const updateHasSignatur = (state) => async (dispatch) => {
    dispatch(setHasPlacedSignature(true));
};

export const deleteSenderFields = (packageData, packageType) => async (dispatch, getState) => {
    const recipients = packageData.recipients || [];
    const senderRecipient = recipients.find((r) => r.user_id === packageData.owner_id);
    const senderRoleId = senderRecipient?.id || null;

    if (!senderRoleId) {
        return;
    }

    const approvalsByDocument = get(getState(), `${packageType}.approvals`, {});
    const senderApprovals = [];
    Object.entries(approvalsByDocument).forEach(([documentId, approvals]) => {
        approvals.forEach((approval) => {
            if (approval.recipient_id === senderRoleId) {
                senderApprovals.push({documentId, approval});
            }
        });
    });

    if (!isEmpty(senderApprovals)) {
        await dispatch(setSelectedInfo({unSelect: true}));
        await Promise.all(senderApprovals.map(({documentId, approval}) => (
            dispatch(deleteApproval({
                packageId: packageData.id,
                documentId,
                approvalId: approval.id
            }))
        )));
    }
};

export const selectSettings = (state) => state.designer.settings;
export const selectCustomFields = (state) => state.designer.customFields;
export const selectValidationTypes = (state) => state.designer.validationTypes;
export const selectCustomFieldUserValues = (state) => state.designer.customFieldUserValues;
export const selectFetchDocumentEque = (state) => state.designer.fetchDocumentEque;
export const selectSelectedInfo = (state) => state.designer.selectedInfo;
export const selectActiveDocument = (state) => {
    if (!state.designer.activeDocument.id) {
        return {
            id: getDefaultActiveDocumentId(state),
            pageIndex: state.designer.activeDocument.pageIndex
        };
    }
    return state.designer.activeDocument || {};
};
export const selectIsFetchingSettings = (state) => state.designer.isFetchingSettings;
export const selectFetchSettingsErrors = (state) => state.designer.fetchSettingsErrors;
export const selectIsSendingToSign = (state) => state.designer.isSendingToSign;
export const selectSendToSignErrors = (state) => state.designer.sendToSignErrors;
export const selectIsUpdatingField = (state) => state.designer.isUpdatingField;
export const selectUpdateApprovalsError = (state) => state.designer.updateApprovalsError;
export const selectIsPreventScroll = (state) => state.designer.isPreventScroll;
export const selectLayouts = (state) => state.designer.layouts;
export const selectLayoutsCount = (state) => state.designer.layoutsMeta?.to;
export const selectLayoutsMeta = (state) => state.designer.layoutsMeta;
export const selectIsDeletingLayout = (state) => state.designer.isDeletingLayout;
export const selectDeleteLayoutError = (state) => state.designer.deleteLayoutError;
export const selectIsFetchingLayouts = (state) => state.designer.isFetchingLayouts;
export const selectFetchLayoutsError = (state) => state.designer.fetchLayoutsError;
export const selectIsSavingLayout = (state) => state.designer.isSavingLayout;
export const selectSaveLayoutError = (state) => state.designer.saveLayoutError;
export const selectIsApplyingLayout = (state) => state.designer.isApplyingLayout;
export const selectApplyLayoutError = (state) => state.designer.applyLayoutError;
export const selectPackageType = (state) => state.designer.packageType;
export const selectUpdateConditonError = (state) => state.designer.updateConditionError;
export const selectHasPlacedSignature = (state) => state.designer.hasPlacedSignature;
export const selectCreateApprovalError = (state) => state.designer.createApprovalError;
export const selectCopiedFieldInfo = (state) => state.designer.copiedFieldInfo;
export const selectDuplicationFieldIdInProgress = (state) => state.designer.duplicationFieldIdInProgress;
export const selectTransactionConditions = (state) => state.designer.transactionConditions;
export const selectHoveredConditionFieldIds = (state) => state.designer.hoveredConditionFieldIds;

export const selectPackageApprovals = (state) => {
    const {packageType} = state.designer;
    return state[packageType]?.approvals || {};
};
export const selectPackageFields = (state) => {
    const {packageType} = state.designer;
    return state[packageType]?.fields || {};
};
export const selectApprovalsForDocument = (documentId) => (state) => {
    const {packageType} = state.designer;
    return state[packageType]?.approvals?.[documentId] || [];
};
export const selectFieldsForApproval = (approvalId) => (state) => {
    const {packageType} = state.designer;
    return state[packageType]?.fields?.[approvalId] || [];
};

export default designerSlice.reducer;
